import { useState } from "react";
import {
  useVerificationInfo,
  useVerifyWebsite,
} from "../../hooks/use-websites.js";
import { useAdvanceWizardStep } from "../../hooks/use-wizard.js";
import { Button } from "../../components/button.js";
import { useToast } from "../../components/toast.js";
import type { WizardState, VerificationMethod } from "../../api/types.js";

function CopyBtn({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="btn btn--secondary btn--sm"
      style={{ marginTop: "0.5rem" }}
      onClick={() => {
        void navigator.clipboard.writeText(text).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        });
      }}
    >
      {copied ? "Copied!" : "Copy"}
    </button>
  );
}

interface VerificationOption {
  value: VerificationMethod;
  title: string;
  description: string;
}

const OPTIONS: VerificationOption[] = [
  {
    value: "dns_txt",
    title: "DNS TXT record",
    description:
      "Add a TXT record to your domain's DNS settings. Best for sites where you control DNS.",
  },
  {
    value: "html_meta",
    title: "HTML meta tag",
    description:
      "Add a <meta> tag to your homepage's <head>. Good if you manage the site's HTML directly.",
  },
  {
    value: "file_upload",
    title: "Verification file",
    description:
      'Upload a small text file to the root of your site at "/.well-known/". Good for CMS-based sites.',
  },
];

interface VerificationStepProps {
  websiteId: string;
  wizardState: WizardState;
}

export function VerificationStep({
  websiteId,
  wizardState,
}: VerificationStepProps) {
  const [selectedMethod, setSelectedMethod] =
    useState<VerificationMethod>("dns_txt");

  const { data: verInfo, isLoading: verInfoLoading } =
    useVerificationInfo(websiteId);
  const verifyMutation = useVerifyWebsite(websiteId);
  const advance = useAdvanceWizardStep(websiteId);
  const { toast } = useToast();

  const isVerified =
    wizardState.steps.find((s) => s.step === "protected_site")?.data
      ?.verified === true;

  async function handleVerify() {
    try {
      const result = await verifyMutation.mutateAsync(selectedMethod);
      if (result.verified) {
        toast("success", "Site ownership verified successfully!");
        advance.mutate({
          step: "protected_site",
          outcome: "pass",
          data: { verified: true, method: selectedMethod },
        });
      }
    } catch {
      // Error is already captured in verifyMutation.error
    }
  }

  function handleSkip() {
    advance.mutate({
      step: "protected_site",
      outcome: "advisory",
      data: { verified: false, skipped: true },
    });
  }

  return (
    <div className="wizard-content">
      <h2 className="wizard-content__title">Verify site ownership</h2>
      <p className="wizard-content__subtitle">
        Prove you control this website before we begin scanning. Choose the
        method that suits your setup. Verification requests identify
        themselves separately from scan traffic.
      </p>

      {/* Method selection */}
      <div className="verification-method-grid" role="radiogroup" aria-label="Verification method">
        {OPTIONS.map((opt) => (
          <div
            key={opt.value}
            className={`verification-card${selectedMethod === opt.value ? " verification-card--selected" : ""}`}
            onClick={() => setSelectedMethod(opt.value)}
            role="radio"
            aria-checked={selectedMethod === opt.value}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ")
                setSelectedMethod(opt.value);
            }}
          >
            <p className="verification-card__title">{opt.title}</p>
            <p className="verification-card__desc">{opt.description}</p>
          </div>
        ))}
      </div>

      {/* Token / instruction detail */}
      {verInfoLoading && (
        <div
          className="loading-text"
          style={{ textAlign: "left", padding: "0.5rem 0" }}
        >
          Loading verification details…
        </div>
      )}

      {verInfo && (
        <div className="wizard-content__detail-block">
          {selectedMethod === "dns_txt" && (
            <>
              <h4>Add this TXT record to your DNS</h4>
              <pre className="code-block">{verInfo.dnsTxtRecord}</pre>
              <CopyBtn text={verInfo.dnsTxtRecord} />
              <p className="text-sm text-muted mt-2">
                DNS changes can take up to 24 hours to propagate.
              </p>
            </>
          )}
          {selectedMethod === "html_meta" && (
            <>
              <h4>Add this tag inside your homepage &lt;head&gt;</h4>
              <pre className="code-block">{verInfo.htmlMetaTag}</pre>
              <CopyBtn text={verInfo.htmlMetaTag} />
            </>
          )}
          {selectedMethod === "file_upload" && (
            <>
              <h4>
                Upload a file to{" "}
                <code
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: "0.8rem",
                  }}
                >
                  {verInfo.filePath}
                </code>
              </h4>
              <p className="text-sm text-muted mb-2">
                File contents (exact, no extra whitespace):
              </p>
              <pre className="code-block">{verInfo.fileContent}</pre>
              <CopyBtn text={verInfo.fileContent} />
            </>
          )}
        </div>
      )}

      {verifyMutation.isSuccess && !verifyMutation.data.verified && (
        <div className="alert alert--error mt-3">
          Verification check failed. Make sure the record/tag/file is in place,
          then try again.
        </div>
      )}

      {verifyMutation.error && (
        <div className="alert alert--error mt-3">
          {verifyMutation.error.message}
        </div>
      )}

      {advance.error && (
        <div className="alert alert--error mt-3">{advance.error.message}</div>
      )}

      {isVerified && (
        <div className="alert alert--success mt-3">
          Site ownership verified.
        </div>
      )}

      <div className="wizard-content__actions">
        <Button
          onClick={handleVerify}
          loading={verifyMutation.isPending || advance.isPending}
          disabled={isVerified}
        >
          {isVerified ? "Verified" : "Verify now"}
        </Button>
        {!isVerified && (
          <Button
            variant="secondary"
            onClick={handleSkip}
            disabled={advance.isPending}
          >
            Skip for now
          </Button>
        )}
      </div>
    </div>
  );
}
